import { useBaseScreenHook } from "../../../../../logic/core/base-screen.hook";
import { Wrapper } from "./OrganizationBillingsInvoiceData.styles";
import { useForm } from "../../../../../logic/core/form.hook";
import React from "react";
import FormField from "../../../../../components/FormField";
import Input from "../../../../../components/Input";
import Select from "../../../../../components/Select";
import { CreditInvoiceData } from "../../../../../logic/credits/credits.model";
import { validateInvoiceData } from "../../../../../logic/credits/credits";
import { useParams } from "react-router-dom";
import { Violations } from "../../../../../logic/core/failures";
import { getLocalizedCountryList, isValidNipPL, sanitizeNipPL, normalizeCountryCode } from "../../../../../logic/core/countryList";

const OrganizationBillingsInvoiceData = ({
    data,
    onClose,
}: { onClose: (data: { cancel?: boolean; invoiceData?: CreditInvoiceData }) => void; data: CreditInvoiceData }) => {
    const { organizationId } = useParams();
    const { t, setLoading, setError, loading, renderError, i18n } = useBaseScreenHook();

    const countryList = getLocalizedCountryList(i18n.language || 'pl');

    const requiredValidator = (v: any) => {
        if (v && typeof v === 'string' && v.trim() !== '') {
            return null;
        }
        return t('validations.required');
    };

    const countryValidator = (v: any) => {
        if (!v || typeof v !== 'string' || v.trim() === '') {
            return t('validations.invalidCountry');
        }
        return null;
    };

    const postalCodeValidator = (v: any) => {
        if (!v || typeof v !== 'string' || v.trim() === '') {
            return t('validations.required');
        }
        const country = formControl('address.country')?.value || 'PL';
        if (country === 'PL' && !/^\d{2}-\d{3}$/.test(v.trim())) {
            return t('validations.postalCodeFormatPL');
        }
        return null;
    };

    const nipValidator = (v: any) => {
        const buyerType = formControl('buyerType')?.value || 'BUSINESS';
        if (buyerType === 'INDIVIDUAL') {
            return null;
        }
        const country = formControl('address.country')?.value || 'PL';
        if (country === 'PL') {
            if (!v || typeof v !== 'string' || v.trim() === '') {
                return t('validations.required');
            }
            if (!isValidNipPL(v.trim())) {
                return t('validations.invalidNIP');
            }
        }
        return null;
    };

    const initialCountry = normalizeCountryCode(data.address?.country) || (data.address?.country === '' ? '' : 'PL');

    const {
        renderValidationMessage,
        formControl,
        formValid,
        formValue,
        setValidations,
    } = useForm<CreditInvoiceData>({
        buyerType: {
            value: data.buyerType || 'BUSINESS'
        },
        companyName: {
            value: data.companyName || '',
            validators: [requiredValidator]
        },
        nip: {
            value: data.nip || '',
            validators: [nipValidator]
        },
        address: {
            controls: {
                country: {
                    value: initialCountry,
                    validators: [countryValidator]
                },
                street: {
                    value: data.address?.street || '',
                    validators: [requiredValidator]
                },
                postalCode: {
                    value: data.address?.postalCode || '',
                    validators: [postalCodeValidator]
                },
                city: {
                    value: data.address?.city || '',
                    validators: [requiredValidator]
                },
            }
        },
        recipientEmail: {
            value: data.recipientEmail || '',
            validators: [requiredValidator]
        },
    });

    const handleBuyerTypeChange = (type: 'BUSINESS' | 'INDIVIDUAL') => {
        formControl('buyerType').patchValue(type);
        if (type === 'INDIVIDUAL') {
            formControl('companyName').patchValue('');
            formControl('nip').patchValue('');
        }
    };

    const _submit = async () => {
        if (!formValid()) {
            return;
        }

        setLoading(true);

        const currentBuyerType = formControl('buyerType').value || 'BUSINESS';
        const rawNip = formValue.nip || '';
        const canonicalNip = currentBuyerType === 'INDIVIDUAL' ? null : (formValue.address?.country === 'PL' ? sanitizeNipPL(rawNip) : rawNip) || null;

        const body: any = {
            buyerType: currentBuyerType,
            companyName: formValue.companyName,
            nip: canonicalNip,
            recipientEmail: formValue.recipientEmail || null,
            address: {
                country: formValue.address?.country || null,
                postalCode: formValue.address?.postalCode || null,
                street: formValue.address?.street || null,
                city: formValue.address?.city || null,
            }
        };

        const res = await validateInvoiceData(organizationId!, body);
        setLoading(false);

        if (res.isViolations()) {
            setValidations((res.failure! as Violations).getValidations());
            return;
        }

        if (res.isFailure()) {
            setError(res.failure!);
            return;
        }

        onClose({
            invoiceData: body
        });
    };

    const isIndividual = formControl('buyerType').value === 'INDIVIDUAL';

    return <Wrapper>
        <div className="title">
            {t('organizationBillingsInvoiceData.title')}
        </div>

        <div className="buyer-type-group">
            <label>
                <input
                    type="radio"
                    name="buyerType"
                    value="BUSINESS"
                    checked={!isIndividual}
                    onChange={() => handleBuyerTypeChange('BUSINESS')}
                />
                {t('organizationBillingsInvoiceData.business')}
            </label>
            <label>
                <input
                    type="radio"
                    name="buyerType"
                    value="INDIVIDUAL"
                    checked={isIndividual}
                    onChange={() => handleBuyerTypeChange('INDIVIDUAL')}
                />
                {t('organizationBillingsInvoiceData.individual')}
            </label>
        </div>

        <FormField>
            <Input label={isIndividual ? t('organizationBillingsInvoiceData.fullName') : t('organizationBillingsInvoiceData.companyName')}>
                <input type="text" value={formControl('companyName').value}
                       onChange={(e) => formControl('companyName').patchValue(e.target.value)}/>
            </Input>
            {renderValidationMessage('companyName')}
        </FormField>

        {!isIndividual && (
            <FormField>
                <Input label={t('organizationBillingsInvoiceData.nip')}>
                    <input type="text" value={formControl('nip').value}
                           onChange={(e) => formControl('nip').patchValue(e.target.value)}/>
                </Input>
                {renderValidationMessage('nip')}
            </FormField>
        )}

        <FormField>
            <div className="label">{t('organizationBillingsInvoiceData.country')}</div>
            <Select
                currentValue={formControl('address.country').value}
                onChange={formControl('address.country').patchValue}
            >
                <option value="">{t('validations.selectCountryPlaceholder')}</option>
                {countryList.map((c) => (
                    <option key={c.code} value={c.code}>
                        {c.name} ({c.code})
                    </option>
                ))}
            </Select>
            {renderValidationMessage('address.country')}
        </FormField>

        <FormField>
            <Input label={t('organizationBillingsInvoiceData.street')}>
                <input type="text" value={formControl('address.street').value}
                       onChange={(e) => formControl('address.street').patchValue(e.target.value)}/>
            </Input>
            {renderValidationMessage('address.street')}
        </FormField>

        <div className="row">
            <div className="zip-code">
                <FormField>
                    <Input label={t('organizationBillingsInvoiceData.postalCode')}>
                        <input type="text" value={formControl('address.postalCode').value}
                               onChange={(e) => formControl('address.postalCode').patchValue(e.target.value)}/>
                    </Input>
                    {renderValidationMessage('address.postalCode')}
                </FormField>
            </div>
            <FormField>
                <Input label={t('organizationBillingsInvoiceData.city')}>
                    <input type="text" value={formControl('address.city').value}
                           onChange={(e) => formControl('address.city').patchValue(e.target.value)}/>
                </Input>
                {renderValidationMessage('address.city')}
            </FormField>
        </div>

        <FormField>
            <Input label={t('organizationBillingsInvoiceData.recipientEmail')}>
                <input type="text" value={formControl('recipientEmail').value}
                       onChange={(e) => formControl('recipientEmail').patchValue(e.target.value)}/>
            </Input>
            {renderValidationMessage('recipientEmail')}
        </FormField>

        <div className="email-hint">
            {t('organizationBillingsInvoiceData.emailHint')}
        </div>

        <div className="buttons">
            <button className={'button text'} disabled={loading} onClick={() => onClose({ cancel: true })}>
                {t('organizationBillingsInvoiceData.backBtn')}
            </button>
            <button className={'button save'} disabled={loading} onClick={_submit}>
                {t('organizationBillingsInvoiceData.saveBtn')}
            </button>
        </div>

        {renderError()}
    </Wrapper>;
};

export default OrganizationBillingsInvoiceData;
